import React, { useEffect, useState, useRef, lazy, Suspense } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { ArrowDownCircle } from 'lucide-react'
import SaveStatusIndicator from '@components/common/SaveStatusIndicator'

// 懒加载搜索弹窗：连带 SearchPanel/fuse.js 搜索链路移出首屏 bundle，打开搜索时才加载
const ModernSearch = lazy(() => import('@components/Search/ModernSearch'))
import CRDTNavigation from '@components/ui/CRDTNavigation'
import { useAuthStore } from '../../auth'
import { MobileUserMenu } from '../../auth/components/MobileUserMenu'
import { useSyncCollectionToRedux } from '../../hooks/useSyncCollectionToRedux'

import { ModeStartupToggle } from '@components/ui/ModeStartupToggle'
import { useUpdateChecker } from '../../hooks/useUpdateChecker'
import { SyncStatusIndicator } from '../../components/SyncStatusIndicator'
import { noteMenuEventManager, type NoteMenuAction } from '../../utils/noteMenuEventManager'
import { isSheetNote } from '@shared/services/sheetNoteFormat'

import { toggleSidebar, toggleBlockReferencePanel, toggleTocOutline, toggleSearch, setSearchOpen, setSidebarOpen, setBlockReferencePanelOpen } from '@store/slices/uiSlice'
import { toggleLeftEditor } from '@store/slices/leftEditorSlice'
import { toggleRightEditor, closeRightEditor, openGraphInRightEditor } from '@store/slices/rightEditorSlice'
import { RootState } from '@store'
import { isTauriMobilePlatform } from '../../utils/mobile'

const Header: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const store = useStore()
  // 只订阅 currentNote 和 selectedBlocks，不订阅 notes 数组（避免笔记内容更新时 Header 不必要重渲染）
  const { currentNote, selectedBlocks } = useSelector((state: RootState) => state.notes)

  // ★ 移动端判定：plugin-os 优先，UA 兜底（避免 Tauri 注入时机/缓存导致 isMobile=false，
  //   误在移动端显示桌面端专属按钮，如知识图谱）
  const isMobile =
    isTauriMobilePlatform() ||
    (typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/.test(navigator.userAgent))

  // 订阅更新下载状态（多实例同步：通过 localStorage + CustomEvent 与其他组件同步）
  const {
    updateInfo,
    downloadStatus,
    installDownloaded,
  } = useUpdateChecker({
    checkOnMount: false,
    skipIfCheckedRecently: false,
  })

  // Electric + TanStack DB 实时同步（数据管道，将集合变更同步到 Redux）
  useSyncCollectionToRedux()

  // 检测当前模式
  const isSujiMode = location.pathname.startsWith('/suji')
  const isCRDTMode = location.pathname.startsWith('/crdt') || location.pathname.startsWith('/notes')

  // 获取编辑模式的目标笔记
  const getEditModeTargetNote = () => {
    // 从 store 懒读取 notes，避免订阅 notes 变化导致 Header 不必要重渲染
    const notes = (store.getState() as RootState).notes.notes
    // 优先使用最近编辑的笔记
    const lastEditedId = localStorage.getItem('last_edited_note_id')
    if (lastEditedId) {
      const note = notes.find(n => n.id === lastEditedId && !n.isDeleted && !n.isArchived && !n.isFolder)
      if (note) return note
    }
    // 否则使用最新更新的普通笔记（排除文件夹）
    const sortedNotes = [...notes]
      .filter((note) => !note.isDeleted && !note.isArchived && !note.isFolder)
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    return sortedNotes[0] || null
  }
  const { sidebarOpen, blockReferencePanelOpen, tocOutlineOpen, searchOpen } = useSelector((state: RootState) => state.ui)
  // ★ 知识图谱共享右槽位（rightEditor.view === 'graph'），与右参考编辑器互斥单例
  const { open: rightEditorOpen, view: rightEditorView } = useSelector((state: RootState) => state.rightEditor)
  const graphPanelOpen = rightEditorOpen && rightEditorView === 'graph'
  // ★ 右参考编辑器按钮激活态：仅编辑器视图高亮（图谱视图打开时不亮，两按钮互斥）
  const editorPanelOpen = rightEditorOpen && rightEditorView === 'editor'
  const { open: leftEditorOpen } = useSelector((state: RootState) => state.leftEditor)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)

  // 获取当前激活笔记 ID（用于更多菜单功能）
  const crdtEditorState = useSelector((state: RootState) => state.crdtMultiNoteEditor)
  const sujiCRDTEditorState = useSelector((state: RootState) => state.sujiCRDTMultiNoteEditor)
  const activeNoteId = isSujiMode
    ? sujiCRDTEditorState.currentNoteId
    : crdtEditorState.currentNoteId

  // ★ 移动端 CRDT 模式"三个点"更多菜单（替代原分享按钮）：分享/历史/导出/删除
  // 经 noteMenuEventManager 命令直达激活笔记所在面板执行（面板是唯一实现方，
  // Header 零业务逻辑），与 deleteNoteEventManager 同款注册/触发模式
  const [moreMenuOpen, setMoreMenuOpen] = useState(false)
  const moreMenuButtonRef = useRef<HTMLButtonElement>(null)
  const [moreMenuPos, setMoreMenuPos] = useState({ top: 0, right: 8 })
  // 电子表格笔记仅支持删除（与面板三点菜单结构一致）：打开菜单时懒读取
  // store（不订阅 notes 数组，保持 Header 不因笔记内容变化重渲染）
  const [moreMenuIsSheet, setMoreMenuIsSheet] = useState(false)

  const openMoreMenu = () => {
    const notes = (store.getState() as RootState).notes.notes
    const note = notes.find((n: any) => n.id === activeNoteId)
    setMoreMenuIsSheet(isSheetNote(note?.content))
    const rect = moreMenuButtonRef.current?.getBoundingClientRect()
    if (rect) {
      // 菜单跟随触发元素（项目弹窗定位约定）：按钮正下方、右缘对齐
      setMoreMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    }
    setMoreMenuOpen(true)
  }

  const handleMoreMenuAction = (action: NoteMenuAction) => {
    if (activeNoteId) {
      noteMenuEventManager.triggerAction(activeNoteId, action)
    }
    setMoreMenuOpen(false)
  }

  // 点击外部关闭三个点菜单（与面板三点菜单同款 outside-click 模式）
  useEffect(() => {
    if (!moreMenuOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (
        !target.closest('[data-header-more-menu]') &&
        !moreMenuButtonRef.current?.contains(target)
      ) {
        setMoreMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [moreMenuOpen])

  // 路由切换 / 激活笔记变化时关闭菜单（避免菜单悬挂在已切换的界面上）
  useEffect(() => {
    setMoreMenuOpen(false)
  }, [location.pathname, activeNoteId])

  // ★ 保存同步指示器（唯一位置，桌面/移动统一）：按 lastActiveNoteId（最近一次
  // 保存的笔记）显示两态状态（正在保存/保存成功）——主/速记/左参考/右参考编辑器
  // 并发时取最近活跃的那个，避免一处位置多状态竞争。selector 返回原始字符串，
  // store 其他部分变化时引用不变，不会引起 Header 重渲染
  const saveStatus = useSelector((state: RootState) => {
    const id = state.saveIndicator?.lastActiveNoteId
    return id ? state.saveIndicator?.status?.[id] : undefined
  })
  // 失败原因（failed 态文案依据：OCC 冲突让位 vs 网络失败重试中）
  const saveFailReason = useSelector((state: RootState) => {
    const id = state.saveIndicator?.lastActiveNoteId
    return id ? state.saveIndicator?.failReason?.[id] : undefined
  })

  // 同时检查 isAuthenticated 和 user，确保用户真正登录
  const isLoggedIn = isAuthenticated && user !== null

  const handleToggleSidebar = () => {
    // 移动端：右参考编辑器打开时，先关闭右参考编辑器，再打开侧边栏
    if (isMobile && rightEditorOpen && !sidebarOpen) {
      dispatch(closeRightEditor())
      dispatch(setSidebarOpen(true))
    } else {
      dispatch(toggleSidebar())
    }
  }

  const handleToggleBlockReferencePanel = () => {
    dispatch(toggleBlockReferencePanel())
  }

  const handleToggleTocOutline = () => {
    dispatch(toggleTocOutline())
  }

  // ★ 知识图谱：共享右槽位单例 —— 已在图谱视图则关闭整个右槽位（联动关闭）；
  //   否则在右参考编辑器位置打开图谱（移动端半屏、桌面右侧栏），并关闭块引用面板避免重叠
  const handleToggleGraphPanel = () => {
    if (graphPanelOpen) {
      dispatch(closeRightEditor())
    } else {
      dispatch(setBlockReferencePanelOpen(false))
      dispatch(openGraphInRightEditor())
    }
  }

  // 全局快捷键监听 ⌘K / Ctrl+K
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        dispatch(toggleSearch())
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [dispatch])

  return (
    <>
      {/* ★ 2026-10-30：移动端/桌面端横向留白分流——此前 px-4 与 safe-area-x
          同在 utilities 层，后者后定义整行覆盖 px-4，env=0 设备上速记/编辑
          按钮直接贴边。现移动端改用 [--sa-x-base:1rem] 在避让区之上叠加
          16px 基础留白（safe-area-x 内部 calc 保证不被层叠吞掉），桌面端
          无避让需求直接 px-4。 */}
      <header className={`bg-white dark:bg-gray-800 ${
        isMobile
          ? 'safe-area-top safe-area-x [--sa-x-base:1rem] pb-0'
          : 'px-4 py-1.5 border-b border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center justify-between">
          {/* Left section */}
          <div className="flex items-center space-x-4">
            {/* Sidebar toggle - visible only when sidebar is closed */}
            {!sidebarOpen && (
              <button
                onClick={handleToggleSidebar}
                className={`${isMobile ? 'mobile-header-btn' : 'p-1.5 rounded-lg'} transition-colors duration-200 ${
                  sidebarOpen
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
                title="切换侧边栏"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4H5a2 2 0 00-2 2v12a2 2 0 002 2h4m0-16h10a2 2 0 012 2v12a2 2 0 01-2 2H9m0-16v16" />
                </svg>
              </button>
            )}

            {/* TOC outline toggle - 移动端隐藏 */}
            {!isMobile && (
            <button
              onClick={handleToggleTocOutline}
              className={`${isMobile ? 'mobile-header-btn' : 'p-1.5 rounded-lg'} transition-colors duration-200 ${tocOutlineOpen ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
              title="显示大纲"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
            </button>
            )}

            {/* 左侧速记栏开关 - 移动端隐藏 */}
            {!isMobile && (
            <button
              onClick={() => dispatch(toggleLeftEditor())}
              className={`${isMobile ? 'mobile-header-btn' : 'p-1.5 rounded-lg'} transition-colors duration-200 ${
                leftEditorOpen
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
              title={leftEditorOpen ? '关闭左侧速记栏' : '打开左侧速记栏'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            )}

            {/* 模式切换：移动端简洁文字切换 / 桌面端分段控制器 */}
            {isMobile ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    localStorage.setItem('last_editor_mode', 'suji')
                    navigate('/suji')
                  }}
                  className={`transition-all duration-200 min-h-[44px] flex items-center whitespace-nowrap flex-shrink-0 ${
                    isSujiMode
                      ? 'text-lg font-semibold text-gray-900 dark:text-white'
                      : 'text-sm text-gray-400 dark:text-gray-500'
                  }`}
                  title="速记模式"
                >
                  速记
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('last_editor_mode', 'crdt')
                    const targetNote = getEditModeTargetNote()
                    if (targetNote) {
                      navigate(`/crdt/notes/${targetNote.id}`)
                    } else {
                      navigate('/notes')
                    }
                  }}
                  className={`transition-all duration-200 min-h-[44px] flex items-center whitespace-nowrap flex-shrink-0 ${
                    isCRDTMode
                      ? 'text-lg font-semibold text-gray-900 dark:text-white'
                      : 'text-sm text-gray-400 dark:text-gray-500'
                  }`}
                  title="编辑模式"
                >
                  编辑
                </button>
              </div>
            ) : (
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                <button
                  onClick={() => {
                    localStorage.setItem('last_editor_mode', 'suji')
                    navigate('/suji')
                  }}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-all duration-200 active:scale-95 ${
                    isSujiMode
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title="速记模式 - 快速记录想法"
                >
                  速记
                </button>
                <button
                  onClick={() => {
                    localStorage.setItem('last_editor_mode', 'crdt')
                    const targetNote = getEditModeTargetNote()
                    if (targetNote) {
                      navigate(`/crdt/notes/${targetNote.id}`)
                    } else {
                      navigate('/notes')
                    }
                  }}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-all duration-200 active:scale-95 ${
                    isCRDTMode
                      ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title="编辑模式"
                >
                  编辑
                </button>
              </div>
            )}

            {/* 保存同步指示器已迁移至 Right section 首位（原"应用版本"位置），
                桌面/移动统一，多编辑器按最近活跃笔记显示 */}

            {/* 右参考编辑器开关 - 移动端隐藏；激活态仅编辑器视图（图谱视图下点击=切回编辑器） */}
            {!isMobile && (
            <button
              onClick={() => dispatch(toggleRightEditor())}
              className={`${isMobile ? 'mobile-header-btn' : 'p-1.5 rounded-lg'} transition-colors duration-200 ${
                editorPanelOpen
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
              title={editorPanelOpen ? '关闭右参考编辑器' : '打开右参考编辑器'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 4v16" />
              </svg>
            </button>
            )}

            {/* ★ 知识图谱 toggle（桌面端）：紧随右参考编辑器按钮——二者共享右槽位、
                互斥单例；Network 节点网络图标体现块引用关联/知识网络 */}
            {!isMobile && (
            <button
              onClick={handleToggleGraphPanel}
              className={`p-1.5 rounded-lg transition-colors duration-200 ${graphPanelOpen ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'}`}
              title={graphPanelOpen ? '关闭知识图谱' : '打开知识图谱'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                {/* 两个节点连接：一对节点 + 连线，体现块引用关联 */}
                <circle cx="18" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <path strokeLinecap="round" d="M15.8 8.2L8.2 15.8" />
              </svg>
            </button>
            )}

            {/* Back to home button - 已隐藏 */}
            <button
              onClick={() => navigate('/')}
              className="hidden flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors duration-200"
              title="Go to home"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="hidden sm:block">Home</span>
            </button>

            {/* TipTap Mobile Test button - 已隐藏 */}
            <button
              onClick={() => navigate('/tiptap-test')}
              className="hidden flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-green-600 dark:text-gray-300 dark:hover:text-green-400 transition-colors duration-200"
              title="TipTap Mobile Test"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:block">Test</span>
            </button>

            {/* App title - hidden */}
            <div className="hidden md:hidden">
              <button
                onClick={() => navigate('/')}
                className="text-xl font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer"
                title="回到首页"
              >
                灵拾笔记
              </button>
            </div>

            {/* Current note title */}
            {currentNote && (
              <div className="hidden lg:block hidden !hidden">
                <span className="text-sm text-gray-500 dark:text-gray-400">Editing:</span>
                <span className="ml-2 text-gray-900 dark:text-white font-medium">
                  {currentNote.title}
                </span>
              </div>
            )}
          </div>

          {/* Right section */}
        <div className="flex items-center space-x-3">
          {/* ★ 保存同步指示器（原"应用版本"位置，桌面/移动统一显示）：
              两态："正在保存"（实际写入 collection）→ "保存成功"（云端持久化确认，
              闪现 2s）。多编辑器（主/速记/左参考/右参考）按 lastActiveNoteId 最近
              保存的笔记显示；固定 min-width 防布局跳动 */}
          {/* ★ 2026-10-30：移动端空闲态（return null）min-w-[64px] 是死空间，
              竖屏 50vw 侧边栏下会挤压速记/编辑按钮换行成竖排；移动端改为
              min-w-0 + overflow-hidden（随内容伸缩），桌面端保留固定宽度
              防按钮跳动。 */}
          <div
            className={`flex items-center justify-center h-5 ${
              isMobile ? 'min-w-0 overflow-hidden' : 'flex-shrink-0 min-w-[64px]'
            }`}
            aria-live="polite"
          >
            <SaveStatusIndicator status={saveStatus} failReason={saveFailReason} />
          </div>

          {/* 下载完成后的立即更新按钮（桌面端） */}
          {!isMobile && downloadStatus === 'downloaded' && updateInfo && (
            <button
              onClick={() => {
                installDownloaded()
              }}
              className="relative flex items-center justify-center w-6 h-6 rounded-full bg-green-500 hover:bg-green-600 text-white transition-colors shadow-sm hover:scale-105 active:scale-95"
              title={`新版本 ${updateInfo.version} 已下载完成，点击立即更新并重启`}
              type="button"
            >
              <ArrowDownCircle className="w-5 h-5" />
            </button>
          )}

          {/* CRDT Navigation */}
          <CRDTNavigation />

          {/* ★ 移动端按钮组：右参考编辑器 + 三个点更多菜单（CRDT 模式）。
              gap-1 紧凑组合——此前两按钮分列在 space-x-3 容器中，视觉间距
              过开（图标间距 32px）；组合后组内图标间距 24px，组外间距不变 */}
          {isMobile && (isCRDTMode || isSujiMode) && (
            <div className="flex items-center gap-1">
              {/* 右参考编辑器开关：图标与桌面端一致（编辑与速记模式均显示）；
                  激活态仅编辑器视图（图谱视图下点击=切回编辑器，与知识图谱按钮互斥） */}
              <button
                onClick={() => dispatch(toggleRightEditor())}
                className={`p-2.5 rounded-lg transition-colors duration-200 ${
                  editorPanelOpen
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                    : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                }`}
                title={editorPanelOpen ? '关闭右参考编辑器' : '打开右参考编辑器'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 4v16" />
                </svg>
              </button>

              {/* 三个点更多菜单（仅编辑模式，替代原分享按钮）：实心点图标与
                  速记模式 AdaptiveEditorTabs 的三个点一致；菜单项命令由激活
                  笔记所在面板执行（分享/历史/导出/删除） */}
              {isCRDTMode && activeNoteId && (
                <button
                  ref={moreMenuButtonRef}
                  onClick={() => (moreMenuOpen ? setMoreMenuOpen(false) : openMoreMenu())}
                  className="p-2.5 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="更多选项"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* ★ 移动端知识图谱开关：已按要求隐藏（桌面端入口不受影响；如需恢复改回
              isMobile && (isCRDTMode || isSujiMode) 即可） */}
          {false && isMobile && (isCRDTMode || isSujiMode) && (
            <button
              onClick={handleToggleGraphPanel}
              className={`p-2.5 rounded-lg transition-colors duration-200 ${
                graphPanelOpen
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
              title={graphPanelOpen ? '关闭知识图谱' : '打开知识图谱'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="18" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <path strokeLinecap="round" d="M15.8 8.2L8.2 15.8" />
              </svg>
            </button>
          )}

          {/* 速记启动模式开关 - 移动端隐藏（移动端已在 App.tsx 强制速记模式） */}
          {!isMobile && <ModeStartupToggle />}

          {/* Electric 连接状态指示器 - 移动端隐藏 */}
          {!isMobile && isLoggedIn && <SyncStatusIndicator />}

          {/* Block reference panel toggle */}
          <div className="hidden">
            <button
              onClick={handleToggleBlockReferencePanel}
              className={`p-2 rounded-lg ${
                blockReferencePanelOpen 
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
              }`}
              title={blockReferencePanelOpen ? 'Hide block references' : 'Show block references'}
              disabled={!selectedBlocks.length}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </button>
          </div>

          {/* Performance demo link */}
          <div className="hidden">
            <button
              onClick={() => navigate('/performance-demo')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              title="性能优化演示"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
          </div>

          {/* 用户菜单 - 桌面右上角与侧边栏底部统一样式/顺序（MobileUserMenu）；顶栏为紧凑模式（仅头像） */}
          {isLoggedIn && !isMobile ? (
            <MobileUserMenu variant="header" />
          ) : !isLoggedIn ? (
            <button
              onClick={() => navigate('/login')}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium min-h-[44px] flex items-center"
            >
              登录
            </button>
          ) : null}
        </div>
      </div>
    </header>

    {/* Modern Search Modal */}
    {searchOpen && (
      <Suspense fallback={null}>
        <ModernSearch isOpen={searchOpen} onClose={() => dispatch(setSearchOpen(false))} />
      </Suspense>
    )}

    {/* ★ 移动端三个点更多菜单（仅编辑模式）：Portal 到 body + fixed 跟随触发按钮
        定位——项目成熟下拉菜单框架（同速记模式 AdaptiveEditorTabs 菜单），彻底
        逃离 Header 内层叠上下文；菜单结构与面板自带三点菜单一致（电子表格笔记
        仅保留删除），命令经 noteMenuEventManager 由激活笔记所在面板执行 */}
    {isMobile && isCRDTMode && moreMenuOpen && activeNoteId && createPortal(
      <div
        data-header-more-menu
        className="fixed bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-[9999] min-w-[160px]"
        style={{ top: moreMenuPos.top, right: moreMenuPos.right }}
      >
        {!moreMenuIsSheet && (
          <>
            <button
              onClick={() => handleMoreMenuAction('share')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              分享
            </button>
            <button
              onClick={() => handleMoreMenuAction('history')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              历史记录
            </button>
            {/* 页内搜索（2026-09-19 用户要求）：与 Ctrl+F 同款 find:toggle 机制，
                命令经面板统一分发（sheet 笔记由面板守卫忽略） */}
            <button
              onClick={() => handleMoreMenuAction('find-in-note')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              页内搜索
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
            <button
              onClick={() => handleMoreMenuAction('export-docx')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              导出为 Word
            </button>
            <button
              onClick={() => handleMoreMenuAction('export-pdf')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              导出为 PDF
            </button>
            <button
              onClick={() => handleMoreMenuAction('export-markdown')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              导出为 Markdown
            </button>
          </>
        )}
        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
        <button
          onClick={() => handleMoreMenuAction('delete')}
          className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          删除笔记
        </button>
      </div>,
      document.body
    )}
  </>
  )
}

export default Header